import { buildCurrencyOptions } from '@choco-admin/utils';
import React, { useMemo } from 'react';

import {
  ChocoCheckbox,
  ChocoSelect,
  Input,
  TextArea,
} from '../../../../../../components/FormComponents';
import { Pill } from '../../../../../../components/Pill';
import { useSupplierAssortmentsDataQuery } from '../../../../../../generated';
import { CoverPhotoUpload } from '../../../CoverPhotoUpload';
import { useSupplierForm } from '../../SupplierFormProvider';
import { type TabProps } from '../../types';

export const ProfileTab: React.FC<TabProps> = ({ mode }) => {
  const { data: assortmentsData, loading: assortmentsLoading } =
    useSupplierAssortmentsDataQuery();

  const {
    supplier,
    formMethods: {
      watch,
      setValue,
      formState: { errors },
    },
  } = useSupplierForm();

  // Watch form fields
  const companyName = watch('name');
  const companyDescription = watch('description');
  const primaryCategory = watch('primaryCategory');
  const secondaryCategories = watch('secondaryCategories') ?? [];
  const deliveryFee = watch('deliveryFee');
  const minimumOrderValue = watch('minimumOrderValue');
  const minimumOrderCurrency = watch('minimumOrderCurrency');
  const minOrderStrict = watch('minOrderStrict');

  // Built once: the list is every ISO 4217 code and does not depend on the form.
  const currencyOptions = useMemo(() => buildCurrencyOptions(), []);

  const toggleCategory = (category: string) => {
    const newCategories = secondaryCategories.includes(category)
      ? secondaryCategories.filter((c: string) => c !== category)
      : [...secondaryCategories, category];
    setValue('secondaryCategories', newCategories, { shouldDirty: true });
  };

  return (
    <div className="mx-auto max-w-4xl">
      {/* Cover photo + logo — edit mode only (uploads need an existing
          supplier id for the presigned URLs; nothing is shown on create).
          The upload flow is fully independent of the form's save path. */}
      {mode === 'edit' && supplier?.id && (
        <CoverPhotoUpload supplier={supplier} />
      )}

      {/* Form Section */}
      <div className="space-y-6">
        {/* Company Name */}
        <Input
          id="name"
          label="Company name"
          value={companyName}
          onChange={(value: string) =>
            setValue('name', value, { shouldDirty: true })
          }
          placeholder="Company name"
          errorMessage={errors.name?.message}
          required
        />

        {/* Company Description */}
        <TextArea
          id="description"
          label="Company description"
          value={companyDescription}
          onChange={(value: string) =>
            setValue('description', value, { shouldDirty: true })
          }
          placeholder="Add a one or two-line description about the distributor here"
          rows={3}
          errorMessage={errors.description?.message}
        />

        {/* Primary Category — a validated dropdown of backend assortments;
            free text let operators save values the backend does not recognize */}
        <ChocoSelect
          id="primaryCategory"
          label="Primary category"
          placeholder="Select primary category"
          options={(() => {
            const assortments =
              assortmentsData?.supplierAssortments.assortments ?? [];
            // Suppliers saved before this was a dropdown may carry a
            // free-text value — keep it selectable so the form loads intact.
            const withCurrent =
              primaryCategory && !assortments.includes(primaryCategory)
                ? [primaryCategory, ...assortments]
                : assortments;
            return withCurrent.map((category: string) => ({
              value: category,
              label: category,
            }));
          })()}
          value={primaryCategory}
          onSelectionChange={(value) =>
            setValue('primaryCategory', value ?? '', { shouldDirty: true })
          }
          isLoading={assortmentsLoading}
          errorMessage={errors.primaryCategory?.message}
          isRequired
        />

        {/* Secondary Categories */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Secondary category
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {assortmentsLoading ? (
              <div>Loading...</div>
            ) : (
              assortmentsData?.supplierAssortments.assortments.map(
                (category: string) => (
                  <Pill
                    key={category}
                    variant={
                      secondaryCategories.includes(category)
                        ? 'primary'
                        : 'default'
                    }
                    className="cursor-pointer"
                    onClick={() => toggleCategory(category)}
                  >
                    {category}
                  </Pill>
                )
              )
            )}
          </div>
          <p className="text-sm text-default-500">Select as many as you like</p>
          {errors.secondaryCategories && (
            <p className="text-danger text-sm mt-1">
              {errors.secondaryCategories.message}
            </p>
          )}
        </div>

        {/* Delivery Fee */}
        <Input
          id="deliveryFee"
          label="Delivery fee"
          value={deliveryFee}
          onChange={(value: string) => {
            const numericValue = value.replace(/[^0-9.]/g, '');
            setValue('deliveryFee', numericValue, { shouldDirty: true });
          }}
          placeholder="Enter delivery fee"
          type="text"
          errorMessage={errors.deliveryFee?.message}
        />

        {/* Minimum Order Value */}
        <div className="flex gap-4">
          <Input
            id="minimumOrderValue"
            label="Minimum order value"
            value={minimumOrderValue}
            onChange={(value: string) => {
              const numericValue = value.replace(/[^0-9.]/g, '');
              setValue('minimumOrderValue', numericValue, {
                shouldDirty: true,
              });
            }}
            placeholder="Enter minimum order value"
            type="text"
            className="flex-grow"
            errorMessage={errors.minimumOrderValue?.message}
          />
          {/* A select, not free text: this is stored verbatim and the backend
              accepts any ISO 4217 code, so a typo became the distributor's
              currency with nothing to catch it. */}
          <ChocoSelect
            id="minimumOrderCurrency"
            label="Currency"
            placeholder="Select currency"
            options={currencyOptions}
            value={minimumOrderCurrency}
            onSelectionChange={(value) =>
              setValue('minimumOrderCurrency', value ?? '', {
                shouldDirty: true,
              })
            }
            className="w-56"
            errorMessage={errors.minimumOrderCurrency?.message}
          />
        </div>

        {/* Strict Minimum Order — eCommerce */}
        <ChocoCheckbox
          isSelected={minOrderStrict}
          onValueChange={(isSelected: boolean) =>
            setValue('minOrderStrict', isSelected, { shouldDirty: true })
          }
          label="Strict minimum order (eCommerce)"
          description="Enforce the minimum order value in the eCommerce checkout. Orders below the minimum will be blocked."
        />
      </div>
    </div>
  );
};
